import { Box, Button, Grid, IconButton, Tab, Tabs, Typography } from "@mui/material";
import { TextField, useGetList, useGetMany, useGetManyReference, useGetOne, useRecordContext } from "react-admin";
import { DrivingLesson, isPlanned, PracticalExam } from "../../../model/autovioCalendarEvents";
import { DateTime } from "luxon";
import { ApprovalStatus, Student } from "../../../providers/studentsProvider";
import type { ExamLocation } from "../../../model/ExamLocation";
import EditIcon from "@mui/icons-material/EditOutlined";
import BlockIcon from "@mui/icons-material/Block";
import { fullWidthTabsStyle, overviewCardStyle } from "../../../misc/backofficeTheme";
import { useEffect, useMemo, useRef, useState } from "react";
import { CarIcon } from "../../../icons/CarIcon";
import { formatDate } from "../../../utils/calendar";
import { isExaminationAssignmentExpired } from "../../drivingSchool/examinationAssignmentUtils";
import { grants } from "../../../backoffice.access_control";
import { LicenseApplicationDialog } from "../../../dialogs/LicenseApplicationDialog";
import { EditPracticalExamLocationDialog } from "../../../dialogs/EditPracticalExamLocationDialog";
import { EditSubscriptionPackageDialog } from "../../../dialogs/EditSubscriptionPackageDialog";
import { useDialog } from "../../../hooks/useDialog";
import { BookedTraining } from "../../../model/BookedTraining";
import { BookedQuotesDialog } from "../../../dialogs/BookedQuotesDialog";
import { AutobahnIcon } from "../../../icons/AutobahnIcon";
import { NachtIcon } from "../../../icons/NachtIcon";
import { UeberlandIcon } from "../../../icons/UeberlandIcon";
import { ManualGearIcon } from "../../../icons/ManualGearIcon";
import { Row } from "../../../misc/Row";
import { Column } from "../../../misc/Column";
import { useSetRecoilState } from "recoil";
import { BookedQuoteDialog, bookedQuoteShownInBookedQuoteDialogState } from "../../../dialogs/BookedQuoteDialog";
import { AddBundleDialog } from "../../../dialogs/AddBundleDialog";
import AddIcon from "@mui/icons-material/Add";
import { SimulatorIcon } from "../../../icons/SimulatorIcon";
import { MoneyField } from "../../../fields/MoneyField";
import { BookedQuote } from "../../../providers/bookedQuotesProvider";
import { FillOutFormButton } from "../../../buttons/FillOutFormButton";
import { useQuery } from "react-query";
import { serverAPI } from "../../../api/server.api";
import { DrivingSchool, drivingSchoolsProvider } from "../../../providers/drivingSchoolsProvider";
import { isForCar } from "../../../model/DrivingLicenseClass";
import _maxBy from "lodash/maxBy";
import _minBy from "lodash/minBy";
import { type DrivingLessonType } from "../../../model/DrivingLessonType";
import { StudentPracticalProgressField } from "../../../fields/StudentPracticalProgressField";
import { TrainingItem } from "./TrainingItem";

function a11yProps(index: number) {
  return {
    id: `tab-${index}`,
    "aria-controls": `tabpanel-${index}`,
  };
}

export function TrainingsCard({ student }: { student: Student }) {
  const [selectedTraining, setSelectedTraining] = useState(student.bookedTrainings[0]);

  return (
    <Box sx={{ ...overviewCardStyle, padding: "24px 30px" }}>
      <BookedTrainingsTabs
        trainings={student.bookedTrainings}
        bf17={student.bf17}
        setSelectedTraining={setSelectedTraining}
      />
      <Practice key={selectedTraining.id} training={selectedTraining} />
      <SubscriptionPackage student={student} />
      <_BookedPackages student={student} />
      <LicenseApplication student={student} training={selectedTraining} />
      <PracticalExamLocation student={student} />
    </Box>
  );
}

export function SubscriptionPackage({ student }: { student: Student }) {
  const { dialogProps, openDialog } = useDialog();
  const {
    subscriptionPackageId,
    subscriptionPackageName,
    subscriptionPackageDisabled,
    subscriptionPackagePausedUntil,
    subscriptionPackagePrice,
  } = student;
  const { data: drivingSchool } = useGetOne<DrivingSchool>(
    "drivingSchools",
    { id: student.drivingSchoolId },
    { enabled: !!subscriptionPackageId },
  );
  const { data: subscriptionPackages } = useQuery(
    ["subscriptionPackages", student.drivingSchoolId],
    () => serverAPI.listSubscriptionPackages(student.drivingSchoolId),
    { enabled: !!subscriptionPackageId },
  );

  if (!subscriptionPackageId) return null;

  const canEditSubscriptionPackage =
    drivingSchool?.customizations.includes("subscriptionPackages") &&
    subscriptionPackages &&
    subscriptionPackages.length > 0;

  return (
    <Box mb={2.5} width="100%" position="relative">
      <Row justifyContent="space-between" alignItems="center">
        <Row gap="4px" alignItems="center">
          <Typography sx={{ color: "text.secondary", fontSize: 12 }}>AUTOVIO Pro Paket</Typography>
          {subscriptionPackageDisabled && <Typography sx={{ fontSize: 13 }}>(Deaktiviert)</Typography>}
        </Row>
        {canEditSubscriptionPackage && (
          <IconButton onClick={openDialog} sx={{ position: "absolute", top: "6px", right: "-8px" }}>
            <EditIcon />
          </IconButton>
        )}
      </Row>
      <Row gap="4px">
        {subscriptionPackageName && <TextField source="subscriptionPackageName" fontSize={14} fontWeight="bold" />}
        {subscriptionPackagePrice?.amount && <MoneyField source="subscriptionPackagePrice" fontSize={14} />}
      </Row>
      {subscriptionPackagePausedUntil && (
        <Typography sx={{ fontSize: 13 }}>{`Pausiert bis ${formatDate(subscriptionPackagePausedUntil)}`}</Typography>
      )}
      {canEditSubscriptionPackage && (
        <EditSubscriptionPackageDialog student={student} subscriptionPackages={subscriptionPackages} {...dialogProps} />
      )}
    </Box>
  );
}

function _BookedPackages({ student }: { student: Student }) {
  const { dialogProps: bookedQuotesDialogProps, openDialog: openBookedQuotesDialog } = useDialog("BookedQuotesDialog");
  const { dialogProps: addBundleDialogProps, openDialog: openAddBundleDialog } = useDialog("AddBundleDialog");
  const setBookedQuoteShownInDialog = useSetRecoilState(bookedQuoteShownInBookedQuoteDialogState);
  const { data: bookedQuotes } = useGetManyReference<BookedQuote>("bookedQuotes", {
    target: "studentId",
    id: student.id,
  });
  const autoOpenBookedQuoteDialogRef = useRef(true);

  useEffect(() => {
    if (!bookedQuotes || !autoOpenBookedQuoteDialogRef.current) {
      return;
    }
    autoOpenBookedQuoteDialogRef.current = false;
    const openDialog = new URL(location.href).searchParams.get("openDialog");
    if (openDialog && openDialog.startsWith("BookedQuoteDialog!")) {
      const bookedQuoteId = openDialog.substring("BookedQuoteDialog!".length);
      const bookedQuote = bookedQuotes.find((it) => it.id === bookedQuoteId);
      if (bookedQuote) {
        setBookedQuoteShownInDialog(bookedQuote);
      }
    }
  }, [bookedQuotes]);

  if (!bookedQuotes || bookedQuotes.length === 0) return null;

  return (
    <Column mb={2.5}>
      <Row justifyContent="space-between" alignItems="flex-start">
        <Box>
          <Typography sx={{ color: "text.secondary", fontSize: 12 }}>Gebuchte Pakete</Typography>
          <Column spacing={0.5}>
            {bookedQuotes.map((quote) => (
              <Typography key={quote.id} variant="body2">
                {quote.bundleName}
              </Typography>
            ))}
          </Column>
        </Box>
        <Box sx={{ mr: "-8px" }}>
          {grants.has("BookedQuotes:list") && bookedQuotes && (
            <IconButton aria-label="Gebuchte Pakete bearbeiten" onClick={openBookedQuotesDialog}>
              <EditIcon />
            </IconButton>
          )}
        </Box>
      </Row>
      <BookedQuotesDialog
        {...bookedQuotesDialogProps}
        student={student}
        addBundleButton={
          <Button startIcon={<AddIcon />} variant="outlined" onClick={openAddBundleDialog}>
            Paket hinzufügen
          </Button>
        }
      />
      <BookedQuoteDialog />
      <AddBundleDialog {...addBundleDialogProps} student={student} />
    </Column>
  );
}

export function BookedTrainingsTabs(props: {
  trainings: BookedTraining[];
  bf17: boolean;
  setSelectedTraining: React.Dispatch<React.SetStateAction<BookedTraining>>;
}) {
  const { trainings, bf17, setSelectedTraining } = props;
  const [selectedTrainingIndex, setSelectedTrainingIndex] = useState(0);

  useEffect(() => {
    setSelectedTraining(trainings[selectedTrainingIndex]);
  }, [selectedTrainingIndex]);

  const showBF17Tag = (training: BookedTraining) => {
    return bf17 && training.isForDrivingLicense && isForCar(training);
  };

  const formatDrivingLicenseClassLabel = (training: BookedTraining) => {
    return `${training.shortLabel}${showBF17Tag(training) ? " (BF17)" : ""}`;
  };

  return (
    <Column sx={{ marginBottom: 2.5 }}>
      <Typography sx={{ fontWeight: "bold", fontSize: 15 }}>Ausbildung</Typography>
      {trainings.length > 1 && (
        <Tabs
          value={selectedTrainingIndex}
          onChange={(_, value) => setSelectedTrainingIndex(value)}
          sx={{ ...fullWidthTabsStyle, mt: "8px" }}
          TabIndicatorProps={{ style: { display: "none" } }}
        >
          {trainings.map((training, index) => (
            <Tab
              key={index}
              aria-label={
                training.isCancelled
                  ? `${formatDrivingLicenseClassLabel(training)}, Abgebrochen`
                  : formatDrivingLicenseClassLabel(training)
              }
              sx={{ borderBottom: "none", flex: 1 }}
              label={
                <Box component="span" sx={{ alignItems: "center", display: "inline-flex", gap: "4px" }}>
                  <Typography component="span" sx={{ fontSize: 14 }}>
                    {formatDrivingLicenseClassLabel(training)}
                  </Typography>
                  {training.isCancelled && (
                    <BlockIcon aria-hidden="true" sx={{ fontSize: 13, opacity: 0.55, pointerEvents: "none" }} />
                  )}
                </Box>
              }
              {...a11yProps(index)}
            />
          ))}
        </Tabs>
      )}
      {trainings.length === 1 && (
        <Column gap="6px">
          <Typography variant="body2" sx={{ color: "text.secondary" }}>
            {`Klasse ${formatDrivingLicenseClassLabel(trainings[0])}`}
          </Typography>
        </Column>
      )}
    </Column>
  );
}

export function LicenseApplication({ student, training }: { student: Student; training: BookedTraining }) {
  const { administrativeOnboardingState, administrativeOnboardingForChangersState } = student;
  const { dialogProps, openDialog } = useDialog("LicenseApplicationDialog");

  let status: ApprovalStatus | undefined;
  if (administrativeOnboardingState) {
    status = administrativeOnboardingState.applicationApproved;
  } else if (administrativeOnboardingForChangersState) {
    status = administrativeOnboardingForChangersState.changeApproved;
  }

  if (!training.isForDrivingLicense || !status) {
    return null;
  }

  const applicationNumber =
    administrativeOnboardingState?.applicationNumber ?? administrativeOnboardingForChangersState?.applicationNumber;
  const examinationAssignmentExpiresAt = student.examinationAssignmentExpiresAt;
  const hasExpiredExaminationAssignment =
    status === "approved" &&
    !!examinationAssignmentExpiresAt &&
    isExaminationAssignmentExpired(examinationAssignmentExpiresAt);

  return (
    <>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 2.5 }}>
        <Box>
          <Typography sx={{ color: "text.secondary", fontSize: 12 }}>
            {administrativeOnboardingState ? "Führerscheinantrag" : "Fahrschulwechsel"}
          </Typography>
          <Column>
            <Typography sx={{ fontSize: 14 }}>
              {status === "approved"
                ? `${hasExpiredExaminationAssignment ? "! Prüfauftrag abgelaufen am" : "Prüfauftrag gültig bis"} ${
                    examinationAssignmentExpiresAt ? formatDate(examinationAssignmentExpiresAt) : "???"
                  }`
                : status === "pending"
                  ? "Genehmigung ausstehend"
                  : status === "notApproved"
                    ? "Genehmigung abgelehnt"
                    : `[${status}]`}
            </Typography>
            {applicationNumber && <Typography sx={{ fontSize: 14 }}>{`Antragsnummer ${applicationNumber}`}</Typography>}
          </Column>
        </Box>
        <Row sx={{ mr: "-8px" }}>
          {grants.has("Student:getFilledOutForm") && <FillOutFormButton student={student} />}
          {grants.has("DrivingLicenseApplication:edit") && (
            <IconButton onClick={openDialog}>
              <EditIcon />
            </IconButton>
          )}
        </Row>
      </Box>
      <LicenseApplicationDialog student={student} {...dialogProps} />
    </>
  );
}

export function Practice({ training }: { training: BookedTraining }) {
  if (!training.isForDrivingLicense) {
    return null;
  }

  const { isCancelled, kpis } = training;

  return (
    <Box sx={{ mb: 2.5 }}>
      <_KPIsGrid training={training} />
      {!isCancelled && Object.keys(kpis?.practicalExams ?? {}).length > 0 && <_PracticalExamInfo training={training} />}
    </Box>
  );
}

function _KPIsGrid(props: { training: BookedTraining }) {
  const { training } = props;
  const student = useRecordContext<Student>();
  const now = useMemo(() => DateTime.now(), []);
  const drivingSchool = drivingSchoolsProvider.getOneFromCache(training.drivingSchoolId);
  const vehicles = drivingSchool?.vehicles ?? [];
  const { data: drivingLessons } = useGetList<DrivingLesson>(
    "studentDrivingLessons",
    {
      filter: {
        studentUid: student.id,
        bookedTrainingIds: [training.id],
        drivingLessonType: ["simulator", "normal", "ueberlandfahrt", "autobahnfahrt", "nachtfahrt"],
        after: now,
        includeDeleted: false,
      },
      pagination: { page: 1, perPage: 1000 },
    },
    { enabled: !!student?.id },
  );
  const areSimulatorDrivingLessonsEnabled = drivingSchool?.customizations.includes("simulatorDrivingLessons");
  const kpis = training?.kpis;
  const finishedDrivingLessonsCount = kpis?.finishedDrivingLessonsCount;
  const compulsorySpecialDrivingLessons = training.compulsorySpecialDrivingLessons ?? {};
  const plannedLessonUnitsByType = useMemo(() => {
    const schaltwagenIds = new Set(
      vehicles.filter((it) => it.type === "car" && it.gearType === "manual").map((v) => v.id),
    );
    const result: Partial<Record<DrivingLessonType | "mitSchaltwagen", number>> = {};
    for (const lesson of (drivingLessons ?? []).filter(isPlanned)) {
      const { drivingLessonType, start, end, vehicleId } = lesson;
      const lessonUnits = end.diff(start).as("minutes") / 45;
      result[drivingLessonType] = (result[drivingLessonType] ?? 0) + lessonUnits;
      if (vehicleId && schaltwagenIds.has(vehicleId)) {
        result.mitSchaltwagen = (result.mitSchaltwagen ?? 0) + lessonUnits;
      }
    }
    return result;
  }, [vehicles, drivingLessons]);
  const muted = !!(training.isCancelled || training.isFinished);

  return (
    <Grid
      container
      columnSpacing={2}
      rowSpacing={1}
      sx={{
        "@media (max-width: 359px)": {
          "& > .MuiGrid-item": { flexBasis: "100%", maxWidth: "100%" },
        },
      }}
    >
      {areSimulatorDrivingLessonsEnabled && training.includesSimulatorDrivingLessons && (
        <Grid item xs={6}>
          <TrainingItem
            icon={<SimulatorIcon />}
            label="Simulator"
            value={finishedDrivingLessonsCount?.simulator ?? 0}
            unit="UE"
            muted={muted}
            plannedLessonUnits={plannedLessonUnitsByType.simulator}
          />
        </Grid>
      )}
      <Grid item xs={6}>
        <TrainingItem
          icon={<CarIcon />}
          label="Übung"
          value={finishedDrivingLessonsCount?.normal ?? 0}
          unit="UE"
          muted={muted}
          plannedLessonUnits={plannedLessonUnitsByType.normal}
        />
      </Grid>
      {training.drivingLicenseClass === "B197" && (
        <Grid item xs={6}>
          <TrainingItem
            icon={<ManualGearIcon />}
            label="Schaltwagen"
            value={kpis?.finishedManualGearLessonsCount ?? 0}
            outOf={10}
            unit="UE"
            muted={muted}
            plannedLessonUnits={plannedLessonUnitsByType.mitSchaltwagen}
          />
        </Grid>
      )}
      {(compulsorySpecialDrivingLessons["Überlandfahrt"] ?? 0) > 0 && (
        <Grid item xs={6}>
          <TrainingItem
            icon={<UeberlandIcon />}
            label="Überland"
            value={finishedDrivingLessonsCount?.ueberlandfahrt ?? 0}
            outOf={compulsorySpecialDrivingLessons["Überlandfahrt"]}
            unit="UE"
            muted={muted}
            plannedLessonUnits={plannedLessonUnitsByType.ueberlandfahrt}
          />
        </Grid>
      )}
      {(compulsorySpecialDrivingLessons["Autobahnfahrt"] ?? 0) > 0 && (
        <Grid item xs={6}>
          <TrainingItem
            icon={<AutobahnIcon />}
            label="Autobahn"
            value={finishedDrivingLessonsCount?.autobahnfahrt ?? 0}
            outOf={compulsorySpecialDrivingLessons["Autobahnfahrt"]}
            unit="UE"
            muted={muted}
            plannedLessonUnits={plannedLessonUnitsByType.autobahnfahrt}
          />
        </Grid>
      )}
      {(compulsorySpecialDrivingLessons["Nachtfahrt"] ?? 0) > 0 && (
        <Grid item xs={6}>
          <TrainingItem
            icon={<NachtIcon />}
            label="Nacht"
            value={finishedDrivingLessonsCount?.nachtfahrt ?? 0}
            outOf={compulsorySpecialDrivingLessons["Nachtfahrt"]}
            unit="UE"
            muted={muted}
            plannedLessonUnits={plannedLessonUnitsByType.nachtfahrt}
          />
        </Grid>
      )}
      <Grid item xs={6}>
        <StudentPracticalProgressField training={training} />
      </Grid>
    </Grid>
  );
}

function _PracticalExamInfo(props: { training: BookedTraining }) {
  const practicalExamIds = Object.keys(props.training.kpis!.practicalExams!);
  const { data: practicalExams } = useGetMany<PracticalExam>("calendarEvents", { ids: practicalExamIds });
  const [numPracticalExamsTaken, lastPracticalExamTakenAt, lastPracticalExamResult, nextPracticalExam] = useMemo(() => {
    const takenPracticalExams = (practicalExams ?? []).filter((it) => !it.deleted && it.endedAt);
    const lastTakenPracticalExam = _maxBy(takenPracticalExams, (it) => it.start.toMillis());
    const scheduledPracticalExams = (practicalExams ?? []).filter(
      (it) => !it.deleted && it.student.rsvp === "accepted" && it.start > DateTime.now(),
    );
    const nextPracticalExam = _minBy(scheduledPracticalExams, (it) => it.start.toMillis());
    return [
      takenPracticalExams.length,
      lastTakenPracticalExam?.start,
      lastTakenPracticalExam?.practicalExamResult,
      nextPracticalExam,
    ];
  }, [practicalExams]);
  return (
    <Box sx={{ mt: 2 }}>
      <Typography sx={{ color: "text.secondary", fontSize: 12 }}>Prüfung bestanden</Typography>
      <Row gap={1}>
        <Typography sx={{ fontSize: 14 }}>
          {(() => {
            if (!practicalExams) {
              // `practicalExams` have not been loaded yet ...
              return "";
            }
            if (!lastPracticalExamTakenAt) {
              if (!nextPracticalExam) {
                return "Nein";
              }
              return `Nein (1. Versuch am ${formatDate(nextPracticalExam.start)})`;
            } else {
              const result = lastPracticalExamResult
                ? lastPracticalExamResult === "passed"
                  ? "Ja"
                  : "Nein"
                : "Unbekannt";
              if (nextPracticalExam) {
                const attempts = numPracticalExamsTaken === 1 ? "ein Versuch" : `${numPracticalExamsTaken} Versuche`;
                return `${result} (${attempts} bisher, nächster am ${formatDate(nextPracticalExam.start)})`;
              } else if (numPracticalExamsTaken === 1) {
                return `${result} (1. Versuch am ${formatDate(lastPracticalExamTakenAt)})`;
              } else {
                return `${result} (${numPracticalExamsTaken} Versuche, letzter am ${formatDate(lastPracticalExamTakenAt)})`;
              }
            }
          })()}
        </Typography>
      </Row>
    </Box>
  );
}

export function PracticalExamLocation({ student }: { student: Student }) {
  const { dialogProps, openDialog } = useDialog();
  const practicalExamLocationUid = student.practicalExamLocationUid;
  const { data: practicalExamLocation } = useGetOne<ExamLocation>(
    "examLocations",
    { id: practicalExamLocationUid!, meta: { drivingSchoolId: student.drivingSchoolId } },
    { enabled: !!practicalExamLocationUid },
  );
  return (
    <>
      <Row sx={{ justifyContent: "space-between", alignItems: "center" }}>
        <Box>
          <Typography sx={{ color: "text.secondary", fontSize: 12 }}>Prüfungsort</Typography>
          <Typography sx={{ fontSize: 14 }}>{practicalExamLocation?.name ?? "–"}</Typography>
        </Box>
        <IconButton onClick={openDialog} sx={{ width: 40, height: 40, flexShrink: 0, mr: "-8px" }}>
          <EditIcon />
        </IconButton>
      </Row>
      <EditPracticalExamLocationDialog student={student} {...dialogProps} />
    </>
  );
}
